import { Module, VuexModule, Mutation, Action } from "vuex-module-decorators";
import BotTypeStats from "@/models/public-bot-stats/BotTypeStats";
import CreatedBotStats from "@/models/public-bot-stats/CreatedBotStats";
import Vue from "vue";
import store from "../store";
import Strategy from "@/utils/strategy";
import { Emit } from "@/api/apiHelper";

/**
 * 🧩 Module which is responsible for managing of public bot stats
 */
@Module
export default class PublicBotStatsModule extends VuexModule {
  // 📦 General bots stats used in the public bots stats overview page
  public botTypesStats: BotTypeStats[] = [];

  // 🔨 Get bot type stats based on the provided strategy Id
  public get getBotTypeStatsById(): (
    strategyId: string
  ) => BotTypeStats | undefined {
    return (strategyId: string) => {
      return this.botTypesStats.find(
        (botStats: BotTypeStats) => botStats.botStrategy == strategyId
      );
    };
  }

  // 🚩 Flag for whether the general bot stats are loading
  public generalBotsStatsAreLoading: boolean = false;

  // 📦 Timestamp of the last time the public statistics were updated
  public lastUpdateTime: number | null = null;

  /**
   * 👾 Mutation which sets the general bot stats
   * @param botStats
   */
  @Mutation
  public setBotTypesStats(botStats: BotTypeStats[]) {
    this.botTypesStats = botStats;
  }

  /**
   * 👾 Mutation which sets the flag for whether the general bot stats are loading
   */
  @Mutation
  public setAreGeneralBotsStatsLoading(isLoading: boolean) {
    this.generalBotsStatsAreLoading = isLoading;
  }

  /**
   * 👾 Mutation which sets the last time the public statistics was updated
   */
  @Mutation
  public setPublicBotStatsLastUpdateTime(lastUpdateTime: number) {
    this.lastUpdateTime = lastUpdateTime;
  }

  /**
   * 🚀 Fetches the placeholder for the strategy
   * @param strategyId
   */
  @Action
  public async fetchStatsPlaceholder(strategyId: string) {
    const strategiesResponse = await Vue.prototype.$api.get(
      "/api/v1/availablestrategies"
    );

    const strategy: Strategy | undefined = (strategiesResponse.data
      .prototypes as Strategy[]).find(
        (strategy: Strategy) => strategy.uuid === strategyId
      );
    this.context.commit("setBotTypesStats", [
      {
        botStrategy: strategy?.uuid,
        name: strategy?.name,
        description: strategy?.description,
        image: strategy?.image,
      },
    ]);
  }

  /**
   * 🚀 Fetches the general public bot strategies
   */
  @Action
  public async fetchGeneralPublicBotStats(properties: any) {
    this.context.commit("setAreGeneralBotsStatsLoading", true);

    try {
      const response = await Vue.prototype.$api.get(
        "/api/v1/publicstrategies",
        {
          seconds: properties.timePeriod,
          enabled_only: properties.enabledOnly,
          market: properties.market,
          min_duration: properties.minimumDuration,
        },
        Emit.None
      );

      let generalBotStats: BotTypeStats[] = [];
      let botStatsPairs = Object.entries(response.data.result);

      if (
        properties.strategyId &&
        (botStatsPairs.length == 0 ||
          botStatsPairs.find((botStatsPair: Object) => {
            return Object.values(botStatsPair)[0] === properties.strategyId;
          }) === undefined)
      ) {
        await this.context.dispatch(
          "fetchStatsPlaceholder",
          properties.strategyId
        );
      } else {
        // ⚠ TODO: Remove this when the stats are no longer linked to the landing page
        // Filter out "Self-learning" and "Buy and Hold" strategies for unauthorized users
        if (!store.state.authorization.isAuthorized) {
          botStatsPairs = botStatsPairs.filter((botStatsPair: Object) => {
            return Object.values(botStatsPair)[0] !== "Buy and Hold";
          });
        }

        // Transform the raw data to General Bots object
        botStatsPairs.forEach((botStatsPair: Object) => {
          let botStrategy = Object.values(botStatsPair)[0];
          let botProperties = Object.values(botStatsPair)[1];

          let specificBotStats: CreatedBotStats[] = [];
          let specificBotStatsPairs = Object.entries(botProperties.bots);

          // Transform the raw data to Created bot object
          specificBotStatsPairs.forEach((botStatsPair: Object) => {
            let botId = Object.values(botStatsPair)[0];
            let botProperties = Object.values(botStatsPair)[1];

            let singleSpecificBotStats = new CreatedBotStats(
              botId,
              botProperties.start_time,
              botProperties.exchange,
              botProperties.market,
              botProperties.trades_made,
              botProperties.days_active,
              botProperties.roi,
              botProperties.bah_roi,
              botProperties.status,
              botProperties.sharpe,
              botProperties.sortino,
              botProperties.maximum_drawdown
            );

            specificBotStats.push(singleSpecificBotStats);
          });

          let performancePerMarket = Object.entries(botProperties?.markets).map(([key, value]) => {
            return {
              market: key,
              averageRoi: (value as any).avg_roi,
              // Remove averaging once it is fixed on the backend
              averageBahRoi: (value as any).avg_bah_roi / (value as any).count,
              count: (value as any).count,
            };
          })

          let bestPerformingMarkets = performancePerMarket
            .sort((a: any, b: any) => b.averageRoi - a.averageRoi)
            .slice(0, 3)

          let singleBotStats = new BotTypeStats(
            botStrategy,
            botProperties.name,
            botProperties.description,
            botProperties.image,
            specificBotStats.length,
            botProperties.trades,
            botProperties.avg_monthly_trade,
            botProperties.avg_duration,
            botProperties.avg_roi_bot,
            botProperties.avg_roi_month,
            botProperties.avg_roi_trades,
            botProperties.avg_bah_roi_month,
            botProperties.day_roi,
            botProperties.week_roi,
            botProperties.month_roi,
            botProperties?.stats?.avg_sharpe,
            botProperties?.stats?.avg_sortino,
            botProperties?.stats?.avg_maximum_drawdown,
            performancePerMarket,
            bestPerformingMarkets,
            specificBotStats
          );
          generalBotStats.push(singleBotStats);
        });
        this.context.commit("setBotTypesStats", generalBotStats);

        // Set the last time the public stats were updated
        let lastUpdateTime = response.data?.stats_time?.end_time;
        if (lastUpdateTime) {
          this.context.commit(
            "setPublicBotStatsLastUpdateTime",
            lastUpdateTime
          );
        }
      }
    } catch (error) {
      await this.context.dispatch(
        "fetchStatsPlaceholder",
        properties.strategyId
      );
    } finally {
      this.context.commit("setAreGeneralBotsStatsLoading", false);
    }
  }
}
